import { useEffect, useState } from 'react';
import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import NewspaperRoundedIcon from '@mui/icons-material/NewspaperRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';

import { AppCard } from '../../shared/components/ui';
import { INoticiaMercado, MercadoService } from '../../shared/services/api/mercado';
import { tempoRelativo } from './formatacao';
import { SecaoGrade } from './SecaoGrade';

const ALTURA_IMAGEM = 118;
const COR_TRIBUTARIO = '#F59E0B';

// Capa da notícia — a imagem do próprio feed quando tem; senão (ou se a
// imagem falhar ao carregar) um fundo em gradiente com ícone, pra todos os
// cards terem a mesma altura.
const CapaNoticia: React.FC<{ noticia: INoticiaMercado }> = ({ noticia }) => {
  const [falhou, setFalhou] = useState(false);
  const tributario = noticia.categoria === 'tributario';

  if (noticia.imagem && !falhou) {
    return (
      <Box
        component="img"
        src={noticia.imagem}
        alt=""
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFalhou(true)}
        sx={{ width: '100%', height: ALTURA_IMAGEM, objectFit: 'cover', display: 'block' }}
      />
    );
  }

  return (
    <Box
      sx={{
        height: ALTURA_IMAGEM,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: tributario
          ? 'linear-gradient(135deg, #78350F 0%, #B45309 100%)'
          : 'linear-gradient(135deg, #003A50 0%, #001E27 100%)',
        color: tributario ? '#FDE68A' : '#20CDF7',
      }}
    >
      {tributario ? <GavelRoundedIcon sx={{ fontSize: 38 }} /> : <TrendingUpRoundedIcon sx={{ fontSize: 38 }} />}
    </Box>
  );
};

export const NoticiasMercado: React.FC = () => {
  const theme = useTheme();
  const [noticias, setNoticias] = useState<INoticiaMercado[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    const buscar = () => {
      MercadoService.getNoticias().then(resultado => {
        setCarregando(false);
        if (resultado instanceof Error) {
          setErro(resultado.message);
          return;
        }
        setErro(null);
        setNoticias(resultado);
      });
    };
    buscar();
    // O backend guarda as notícias por 30 min — atualizar mais que isso só
    // devolveria a mesma lista.
    const timer = setInterval(buscar, 30 * 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <SecaoGrade
      titulo="Notícias do comércio e da economia"
      subtitulo="(fontes nacionais, atualizadas a cada 30 min)"
      icone={<NewspaperRoundedIcon fontSize="small" />}
      larguraMinimaCard={250}
    >
      {carregando && Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} variant="rounded" height={ALTURA_IMAGEM + 96} />
      ))}
      {!carregando && noticias.length === 0 && (
        <Typography variant="body2" color="text.secondary" py={2} sx={{ gridColumn: '1 / -1' }}>
          {erro || 'Nenhuma notícia disponível no momento.'}
        </Typography>
      )}
      {noticias.map(noticia => {
        const tributario = noticia.categoria === 'tributario';
        return (
          <AppCard
            key={noticia.link}
            onClick={() => window.open(noticia.link, '_blank', 'noopener,noreferrer')}
            sx={{
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              ...(tributario && { borderColor: COR_TRIBUTARIO, borderLeft: `4px solid ${COR_TRIBUTARIO}` }),
            }}
          >
            <CapaNoticia noticia={noticia} />
            <Box p={1.25} display="flex" flexDirection="column" gap={0.75} flex={1}>
              <Typography
                variant="body2"
                fontWeight={600}
                title={noticia.resumo || noticia.titulo}
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  fontSize: 13,
                  lineHeight: 1.4,
                  minHeight: 13 * 1.4 * 3,
                }}
              >
                {noticia.titulo}
              </Typography>
              <Typography
                variant="caption"
                noWrap
                sx={{ mt: 'auto', fontSize: 11, color: tributario ? COR_TRIBUTARIO : theme.palette.text.secondary, fontWeight: tributario ? 700 : 400 }}
              >
                {tributario ? 'Alerta tributário · ' : ''}{noticia.fonte} · {tempoRelativo(noticia.publicadoEm)}
              </Typography>
            </Box>
          </AppCard>
        );
      })}
    </SecaoGrade>
  );
};
